/**
 * THE SET SPEAKS — dedicated cinematic casting page.
 *
 * Standalone route: /the-set-speaks
 * - Hero with poster background
 * - "Who We're Looking For" 10-card grid
 * - 5-step multi-step casting form (rendered inline, not as modal)
 * - Thank-you screen after submit
 *
 * Posts to the generic /api/intake/set-speaks endpoint.
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Upload,
  Video,
  AlertCircle,
  Crown,
  Users,
  Star,
  Mic2,
  ShieldCheck,
  PlayCircle,
  X,
} from "lucide-react";

const POSTER =
  "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/fsq1ep9o_102609F8-756D-4BDE-920D-A04F846FDB7E.png";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { label: "MC Members", icon: ShieldCheck, applyingAs: "MC Member" },
  { label: "SC Members", icon: ShieldCheck, applyingAs: "SC Member" },
  { label: "Founders & Presidents", icon: Crown, applyingAs: "Founder / President" },
  { label: "First Ladies", icon: Star, applyingAs: "First Lady" },
  { label: "Veterans / OGs", icon: Star, applyingAs: "Veteran / OG" },
  { label: "New Generation Riders", icon: Users, applyingAs: "New Generation Rider" },
  { label: "Independents", icon: Users, applyingAs: "Independent" },
  { label: "Former Members", icon: Users, applyingAs: "Former Member" },
  { label: "Event Hosts", icon: Mic2, applyingAs: "Event Host" },
  { label: "Culture Influencers", icon: Mic2, applyingAs: "Culture Influencer" },
];

const APPLYING_AS = [
  "MC Member",
  "SC Member",
  "Founder / President",
  "First Lady",
  "Veteran / OG",
  "New Generation Rider",
  "Independent",
  "Former Member",
  "Event Host",
  "Culture Influencer",
];

const ERAS = [
  "Pre-social media era",
  "Facebook era",
  "Instagram/TikTok era",
  "Current generation",
];

const TOPICS = [
  "Respect",
  "Protocol",
  "Politics",
  "Women in the set",
  "Brotherhood",
  "Sisterhood",
  "Leadership",
  "Social media",
  "Money and influence",
  "Community work",
  "Old school vs new school",
  "Other",
];

const STEPS = [
  { key: "basic", title: "Basic Information" },
  { key: "background", title: "Culture Background" },
  { key: "perspective", title: "Your Perspective" },
  { key: "media", title: "Media Uploads" },
  { key: "permissions", title: "Permissions" },
];

// ---------- Helpers ----------
const goldChip =
  "font-accent text-[10px] tracking-[0.28em] text-[#C8A24A] border border-[#C8A24A]/40 bg-[#C8A24A]/5 px-3 py-1";

async function fileToDataUrl(file, max = 1600) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(max / img.width, max / img.height, 1);
        const c = document.createElement("canvas");
        c.width = img.width * ratio;
        c.height = img.height * ratio;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        res({ data: c.toDataURL("image/jpeg", 0.85), name: file.name });
      };
      img.onerror = rej;
      img.src = r.result;
    };
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

// ---------- Page ----------
export default function TheSetSpeaks() {
  const [step, setStep] = useState(-1); // -1 = before form / hero visible. 0..4 = form steps. 5 = thank you.
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    club_name: "",
    club_position: "",
    city: "",
    state: "",
    phone: "",
    email: "",
    applying_as: "",
    years_involved: "",
    currently_active: "",
    era: "",
    changed_most: "",
    miss_about: "",
    misunderstood: "",
    should_come_back: "",
    passionate_topic: "",
    topics: [],
    photo: null, // {data, name}
    video_file_id: null,
    video_filename: "",
    video_url: "",
    permission: false,
  });

  const update = (patch) => {
    setError("");
    setForm((f) => ({ ...f, ...patch }));
  };

  const scrollToForm = () => {
    setStep((s) => (s < 0 ? 0 : s));
    setTimeout(() => {
      document.getElementById("set-speaks-form")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  // Click on a "Who We're Looking For" card → pre-fill Applying As + jump
  // straight into the casting form.
  const pickRole = (applyingAs) => {
    update({ applying_as: applyingAs });
    scrollToForm();
  };

  const validate = () => {
    if (step === 0) {
      if (
        !form.full_name.trim() ||
        !form.city.trim() ||
        !form.state.trim() ||
        !form.phone.trim() ||
        !form.email.trim()
      )
        return "Please fill in all required fields.";
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email.";
    }
    if (step === 1) {
      if (
        !form.applying_as ||
        !form.years_involved ||
        !form.currently_active ||
        !form.era
      )
        return "Please answer all four questions.";
    }
    if (step === 2) {
      const required = [
        form.changed_most,
        form.miss_about,
        form.misunderstood,
        form.should_come_back,
        form.passionate_topic,
      ];
      if (required.some((v) => !v.trim()))
        return "Please share your perspective on all five questions.";
      if (form.topics.length === 0)
        return "Select at least one topic you want to speak on.";
    }
    if (step === 3) {
      if (!form.photo) return "Please upload a recent photo.";
      if (!form.video_file_id && !form.video_url.trim())
        return "Please upload a 1–3 minute introduction video or paste a link.";
    }
    if (step === 4) {
      if (!form.permission) return "Please confirm the permission checkbox.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) return setError(err);
    setStep((s) => s + 1);
    window.scrollTo({ top: document.getElementById("set-speaks-form")?.offsetTop || 0, behavior: "smooth" });
  };
  const back = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const item = await fileToDataUrl(f);
      update({ photo: item });
    } catch {
      setError("Could not read photo. Try a different file.");
    }
  };

  const onVideoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) return setError("Video must be under 100MB.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/applications/video`, fd);
      update({ video_file_id: data.file_id, video_filename: f.name });
    } catch {
      setError("Video upload failed. Try a smaller file or paste a link instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    const err = validate();
    if (err) return setError(err);
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        tags: [
          form.applying_as.toLowerCase().replace(/\s+/g, "_"),
          form.era.toLowerCase().replace(/\W+/g, "_").replace(/^_|_$/g, ""),
          ...form.topics.map((t) =>
            t.toLowerCase().replace(/\W+/g, "_").replace(/^_|_$/g, ""),
          ),
        ].filter(Boolean),
        photos: form.photo ? [form.photo] : [],
        video_file_id: form.video_file_id,
        video_url: form.video_url.trim() || null,
        data: {
          club_name: form.club_name.trim(),
          club_position: form.club_position.trim(),
          applying_as: form.applying_as,
          years_involved: form.years_involved,
          currently_active: form.currently_active,
          era: form.era,
          changed_most: form.changed_most.trim(),
          miss_about: form.miss_about.trim(),
          misunderstood: form.misunderstood.trim(),
          should_come_back: form.should_come_back.trim(),
          passionate_topic: form.passionate_topic.trim(),
          topics: form.topics,
        },
      };
      await axios.post(`${API}/intake/set-speaks`, payload);
      setStep(5);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08070a] text-white set-speaks">
      <Hero
        onApply={scrollToForm}
        onStory={scrollToForm}
        onTrailer={() => setTrailerOpen(true)}
      />
      <WhoWeWant selectedRole={form.applying_as} onPickRole={pickRole} />

      <section
        id="set-speaks-form"
        className="relative py-24 md:py-28 px-6 border-t border-[#C8A24A]/15"
      >
        <div className="max-w-3xl mx-auto">
          {step === 5 ? (
            <ThankYou />
          ) : (
            <>
              <div className="mb-10">
                <div className="font-accent text-[11px] tracking-[0.28em] text-[#C8A24A] mb-3">
                  CASTING APPLICATION · STEP {Math.max(step, 0) + 1} / {STEPS.length}
                </div>
                <h2 className="font-heading text-3xl md:text-5xl font-black">
                  {STEPS[Math.max(step, 0)].title}
                </h2>
                {/* Progress bar */}
                <div className="mt-6 h-[2px] w-full bg-white/10">
                  <div
                    className="h-full bg-[#C8A24A] transition-all"
                    style={{
                      width: `${((Math.max(step, 0) + 1) / STEPS.length) * 100}%`,
                    }}
                  />
                </div>
                {form.applying_as && (
                  <div
                    className="mt-5 inline-flex items-center gap-2.5 border border-[#C8A24A]/50 bg-[#C8A24A]/[0.06] pl-3 pr-2 py-2"
                    data-testid="ss-applying-as-chip"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#C8A24A]" />
                    <span className="font-accent text-[10px] tracking-[0.28em] text-white/55">
                      APPLYING AS
                    </span>
                    <span className="font-heading text-sm font-black text-[#C8A24A]">
                      {form.applying_as}
                    </span>
                    <button
                      type="button"
                      onClick={() => update({ applying_as: "" })}
                      data-testid="ss-applying-as-chip-clear"
                      className="ml-1 w-5 h-5 inline-flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                      aria-label="Clear pre-selected role"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-5"
                >
                  {step <= 0 && <StepBasic form={form} update={update} />}
                  {step === 1 && <StepBackground form={form} update={update} />}
                  {step === 2 && <StepPerspective form={form} update={update} />}
                  {step === 3 && (
                    <StepMedia
                      form={form}
                      update={update}
                      onPhoto={onPhotoChange}
                      onVideo={onVideoChange}
                      uploading={submitting}
                    />
                  )}
                  {step === 4 && <StepPermissions form={form} update={update} />}
                </motion.div>
              </AnimatePresence>

              {error && (
                <div className="mt-6 flex items-center gap-2 border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 text-sm">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}

              <div className="mt-10 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={back}
                  disabled={step <= 0 || submitting}
                  data-testid="set-speaks-back"
                  className="inline-flex items-center gap-2 text-white/60 hover:text-white font-accent text-xs tracking-[0.22em] disabled:opacity-30"
                >
                  <ArrowLeft className="w-4 h-4" /> BACK
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="set-speaks-next"
                    className="inline-flex items-center gap-2 bg-[#C8A24A] text-black font-black px-7 py-3 uppercase tracking-[0.22em] text-xs hover:scale-[1.02] transition-transform"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="set-speaks-submit"
                    className="inline-flex items-center gap-2 bg-[#C8A24A] text-black font-black px-9 py-4 uppercase tracking-[0.22em] text-sm disabled:opacity-60 hover:scale-[1.02] transition-transform"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                      </>
                    ) : (
                      <>Submit Application <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />

      <TrailerComingSoonModal
        open={trailerOpen}
        onClose={() => setTrailerOpen(false)}
      />
    </div>
  );
}

function TrailerComingSoonModal({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[120] flex items-center justify-center px-5"
          data-testid="set-speaks-trailer-modal"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative max-w-lg w-full bg-[#0c0a10] border border-[#C8A24A]/40 p-8 sm:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid="set-speaks-trailer-close"
              className="absolute top-4 right-4 text-white/50 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full border border-[#C8A24A]/60 flex items-center justify-center">
                <PlayCircle className="w-7 h-7 text-[#C8A24A]" />
              </div>
            </div>

            <div className="text-center font-accent text-[11px] tracking-[0.32em] text-[#C8A24A] mb-3 uppercase">
              An AutoMotoVille Original
            </div>
            <h3 className="text-center font-heading font-black text-2xl sm:text-3xl mb-3 uppercase tracking-tight">
              Trailer dropping soon.
            </h3>
            <p className="text-center text-white/70 text-sm leading-relaxed mb-6">
              We're still gathering footage from the people who lived this
              culture. The trailer will premiere here once cameras are off and
              the cut is locked.
            </p>
            <p className="text-center text-white/50 text-xs italic mb-7">
              In the meantime — if you ride, lead, or remember the original
              code — your story belongs in this film.
            </p>

            <button
              onClick={onClose}
              data-testid="set-speaks-trailer-dismiss"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C8A24A] text-black font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:scale-[1.02] transition-transform"
            >
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ---------- Sections ----------
function Hero({ onApply, onStory, onTrailer }) {
  return (
    <section
      className="relative min-h-[100vh] flex items-end overflow-hidden"
      data-testid="set-speaks-hero"
    >
      {/* Poster */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${POSTER})` }}
      />
      {/* Dark overlays for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-black/40" />
      {/* Subtle pink + blue glow accents */}
      <div
        className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full opacity-20 pointer-events-none blur-3xl"
        style={{ background: "radial-gradient(circle, #FF2D9C, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full opacity-15 pointer-events-none blur-3xl"
        style={{ background: "radial-gradient(circle, #00E5FF, transparent 70%)" }}
      />

      <div className="relative z-10 w-full px-6 sm:px-10 lg:px-20 py-20 lg:py-28">
        <Link
          to="/"
          className="font-accent text-[11px] tracking-[0.28em] text-[#C8A24A] hover:text-white inline-flex items-center gap-2 mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> AUTOMOTOVILLE ORIGINAL
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className={goldChip + " inline-block mb-6"}>
            NOW CASTING · MC & SC CLUB MEMBERS
          </div>
          <div
            className="font-accent text-[12px] sm:text-sm tracking-[0.32em] text-[#C8A24A]/90 mb-4 uppercase"
            data-testid="set-speaks-eyebrow"
          >
            Old laws. New era. One culture still speaking.
          </div>
          <h1
            className="font-heading font-black leading-[0.92] tracking-tight text-[68px] sm:text-[110px] lg:text-[160px] uppercase"
            style={{
              backgroundImage:
                "linear-gradient(180deg, #f4eedf 0%, #d6c896 60%, #8a6f29 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 80px rgba(200,162,74,0.18)",
            }}
            data-testid="set-speaks-title"
          >
            The Set
            <br />
            Speaks.
          </h1>
          <div className="mt-5 font-accent tracking-[0.28em] text-sm sm:text-base text-[#C8A24A]">
            EVERY PATCH HAS A PERSPECTIVE.
          </div>
          <p className="mt-6 max-w-2xl text-white/80 leading-relaxed text-base sm:text-lg space-y-2">
            <span className="block">The motorcycle set has changed.</span>
            <span className="block">Some say the culture evolved.</span>
            <span className="block">Others say the code was lost.</span>
            <span className="block text-white">
              Now the people who lived it are finally speaking.
            </span>
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onApply}
              data-testid="set-speaks-apply"
              className="inline-flex items-center justify-center gap-2 bg-[#C8A24A] text-black font-black px-8 py-4 uppercase tracking-[0.28em] text-sm hover:scale-[1.03] transition-transform"
            >
              Apply Now <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onTrailer}
              data-testid="set-speaks-trailer"
              className="inline-flex items-center justify-center gap-2 border border-[#C8A24A]/60 text-[#C8A24A] font-black px-8 py-4 uppercase tracking-[0.28em] text-sm hover:bg-[#C8A24A]/10 transition-colors"
            >
              <PlayCircle className="w-4 h-4" /> Watch Trailer
            </button>
            <button
              onClick={onStory}
              data-testid="set-speaks-story"
              className="inline-flex items-center justify-center gap-2 border border-[#C8A24A]/60 text-[#C8A24A] font-black px-8 py-4 uppercase tracking-[0.28em] text-sm hover:bg-[#C8A24A]/10 transition-colors"
            >
              Submit Your Story
            </button>
          </div>

          <div className="mt-12 font-accent text-[11px] tracking-[0.34em] text-white/50">
            AN AUTOMOTOVILLE ORIGINAL
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function WhoWeWant({ selectedRole, onPickRole }) {
  return (
    <section
      className="relative py-24 md:py-28 px-6 bg-[#08070a]"
      data-testid="set-speaks-who"
    >
      <div className="max-w-6xl mx-auto">
        <div className="font-accent text-[11px] tracking-[0.28em] text-[#C8A24A] mb-3">
          / CASTING CALL
        </div>
        <h2 className="font-heading text-4xl md:text-6xl font-black uppercase tracking-tight">
          Who We're
          <br />
          Looking For.
        </h2>
        <div className="mt-5 font-accent text-[11px] tracking-[0.28em] text-white/45">
          TAP A ROLE TO START YOUR APPLICATION
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mt-10">
          {ROLES.map((r, i) => {
            const Icon = r.icon;
            const isSelected = selectedRole && selectedRole === r.applyingAs;
            return (
              <motion.button
                key={r.label}
                type="button"
                onClick={() => onPickRole && onPickRole(r.applyingAs)}
                aria-pressed={isSelected}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 5) * 0.06 }}
                className={`group relative text-left border transition-all p-5 flex items-start gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8A24A]/60 ${
                  isSelected
                    ? "border-[#C8A24A] bg-[#C8A24A]/[0.08] shadow-[0_0_0_1px_rgba(200,162,74,0.45)_inset,0_0_30px_-10px_rgba(200,162,74,0.45)]"
                    : "border-white/10 bg-white/[0.02] hover:border-[#C8A24A]/60 hover:bg-white/[0.05] hover:-translate-y-0.5"
                }`}
                data-testid={`role-card-${i}`}
                data-role={r.applyingAs}
                data-selected={isSelected ? "true" : "false"}
              >
                <span
                  className={`shrink-0 w-9 h-9 border flex items-center justify-center transition-colors ${
                    isSelected
                      ? "border-[#C8A24A] bg-[#C8A24A]/15 text-[#C8A24A]"
                      : "border-[#C8A24A]/40 text-[#C8A24A] group-hover:bg-[#C8A24A]/10"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-black text-sm md:text-base leading-tight">
                    {r.label}
                  </div>
                  {isSelected && (
                    <div
                      className="mt-1.5 font-accent text-[9px] tracking-[0.28em] text-[#C8A24A] inline-flex items-center gap-1"
                      data-testid={`role-card-selected-${i}`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> SELECTED
                    </div>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-14 max-w-3xl">
          <div className="font-accent text-[11px] tracking-[0.28em] text-white/50 mb-3">
            WE WANT REAL PERSPECTIVES ON
          </div>
          <p className="text-white/80 leading-relaxed text-base md:text-lg">
            brotherhood, sisterhood, respect, protocol, politics, social media,
            leadership, women in the culture, what changed, what needs to come
            back, and the future of the set.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------- Steps ----------
const labelCls =
  "block font-accent text-[10px] tracking-[0.28em] text-white/55 mb-2";
const inputCls =
  "w-full bg-black/40 border border-white/10 px-4 py-3 text-sm text-white outline-none focus:border-[#C8A24A] focus:ring-1 focus:ring-[#C8A24A]/30";

function StepBasic({ form, update }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="FULL NAME *">
        <input
          className={inputCls}
          value={form.full_name}
          onChange={(e) => update({ full_name: e.target.value })}
          data-testid="ss-full_name"
        />
      </Field>
      <Field label="CLUB NAME">
        <input
          className={inputCls}
          value={form.club_name}
          onChange={(e) => update({ club_name: e.target.value })}
          data-testid="ss-club_name"
        />
      </Field>
      <Field label="CLUB POSITION">
        <input
          className={inputCls}
          placeholder="President, VP, Road Captain, Sergeant at Arms…"
          value={form.club_position}
          onChange={(e) => update({ club_position: e.target.value })}
          data-testid="ss-club_position"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="CITY *">
          <input
            className={inputCls}
            value={form.city}
            onChange={(e) => update({ city: e.target.value })}
            data-testid="ss-city"
          />
        </Field>
        <Field label="STATE *">
          <input
            className={inputCls}
            value={form.state}
            onChange={(e) => update({ state: e.target.value })}
            data-testid="ss-state"
          />
        </Field>
      </div>
      <Field label="PHONE NUMBER *">
        <input
          className={inputCls}
          inputMode="tel"
          value={form.phone}
          onChange={(e) => update({ phone: e.target.value })}
          data-testid="ss-phone"
        />
      </Field>
      <Field label="EMAIL ADDRESS *">
        <input
          className={inputCls}
          type="email"
          value={form.email}
          onChange={(e) => update({ email: e.target.value })}
          data-testid="ss-email"
        />
      </Field>
    </div>
  );
}

function StepBackground({ form, update }) {
  return (
    <div className="space-y-5">
      <Field label="APPLYING AS *">
        <select
          className={inputCls}
          value={form.applying_as}
          onChange={(e) => update({ applying_as: e.target.value })}
          data-testid="ss-applying_as"
        >
          <option value="">Select…</option>
          {APPLYING_AS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
      <Field label="YEARS INVOLVED IN THE CULTURE *">
        <input
          className={inputCls}
          inputMode="numeric"
          value={form.years_involved}
          onChange={(e) => update({ years_involved: e.target.value })}
          data-testid="ss-years"
        />
      </Field>
      <Field label="CURRENTLY ACTIVE? *">
        <div className="flex gap-2">
          {["Yes", "No"].map((o) => {
            const active = form.currently_active === o;
            return (
              <button
                key={o}
                type="button"
                onClick={() => update({ currently_active: o })}
                data-testid={`ss-active-${o.toLowerCase()}`}
                className={`flex-1 px-4 py-3 border text-sm transition-all ${
                  active
                    ? "border-[#C8A24A] bg-[#C8A24A]/10 text-[#C8A24A]"
                    : "border-white/10 bg-white/[0.02] text-white/75 hover:border-white/30"
                }`}
              >
                {o}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="ERA YOU CAME INTO THE CULTURE *">
        <select
          className={inputCls}
          value={form.era}
          onChange={(e) => update({ era: e.target.value })}
          data-testid="ss-era"
        >
          <option value="">Select…</option>
          {ERAS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}

function StepPerspective({ form, update }) {
  const longQs = [
    ["changed_most", "What do you think changed the set the most?"],
    ["miss_about", "What do you miss about the old culture?"],
    ["misunderstood", "What do people misunderstand about MC/SC life?"],
    ["should_come_back", "What should come back?"],
    ["passionate_topic", "What topic are you most passionate about discussing?"],
  ];
  const toggleTopic = (t) => {
    const has = form.topics.includes(t);
    update({ topics: has ? form.topics.filter((x) => x !== t) : [...form.topics, t] });
  };
  return (
    <div className="space-y-5">
      {longQs.map(([k, q]) => (
        <Field key={k} label={q.toUpperCase() + " *"}>
          <textarea
            rows={4}
            maxLength={2000}
            className={inputCls}
            value={form[k]}
            onChange={(e) => update({ [k]: e.target.value })}
            data-testid={`ss-${k}`}
          />
        </Field>
      ))}
      <div>
        <div className={labelCls}>TOPICS YOU WANT TO SPEAK ON *</div>
        <div className="flex flex-wrap gap-2">
          {TOPICS.map((t) => {
            const on = form.topics.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTopic(t)}
                data-testid={`ss-topic-${t.toLowerCase().replace(/\W+/g, "-")}`}
                className={`px-3 py-1.5 text-sm border transition-all ${
                  on
                    ? "border-[#C8A24A] bg-[#C8A24A]/15 text-[#C8A24A]"
                    : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StepMedia({ form, update, onPhoto, onVideo, uploading }) {
  return (
    <div className="space-y-6">
      <p className="text-white/65 text-sm leading-relaxed">
        Tell us who you are, what part of the culture you represent, and why
        your voice matters.
      </p>

      <div>
        <div className={labelCls}>RECENT PHOTO *</div>
        <label
          className="flex items-center gap-3 border-2 border-dashed border-white/15 hover:border-[#C8A24A]/60 px-5 py-6 cursor-pointer transition-colors"
          data-testid="ss-photo-label"
        >
          <Upload className="w-5 h-5 text-[#C8A24A]" />
          <span className="text-sm text-white/75 flex-1">
            {form.photo ? form.photo.name : "Upload a recent photo (no filters)"}
          </span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPhoto}
            data-testid="ss-photo-input"
          />
        </label>
        {form.photo && (
          <img
            src={form.photo.data}
            alt="preview"
            className="mt-3 w-32 h-32 object-cover border border-white/10"
          />
        )}
      </div>

      <div>
        <div className={labelCls}>1–3 MINUTE VIDEO INTRODUCTION *</div>
        <label
          className="flex items-center gap-3 border-2 border-dashed border-white/15 hover:border-[#C8A24A]/60 px-5 py-6 cursor-pointer transition-colors"
          data-testid="ss-video-label"
        >
          <Video className="w-5 h-5 text-[#C8A24A]" />
          <span className="text-sm text-white/75 flex-1">
            {form.video_filename
              ? `Uploaded: ${form.video_filename}`
              : "Upload a 1–3 minute video"}
          </span>
          {uploading && <Loader2 className="w-4 h-4 animate-spin text-white/60" />}
          <input
            type="file"
            accept="video/*"
            className="hidden"
            onChange={onVideo}
            data-testid="ss-video-input"
          />
        </label>
        <div className="mt-3">
          <Field label="OR PASTE A VIDEO LINK">
            <input
              className={inputCls}
              placeholder="YouTube / Vimeo / Drive / Dropbox"
              value={form.video_url}
              onChange={(e) => update({ video_url: e.target.value })}
              data-testid="ss-video-url"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

function StepPermissions({ form, update }) {
  return (
    <div className="space-y-5">
      <label
        className="flex items-start gap-3 border border-white/10 bg-white/[0.02] p-5 cursor-pointer hover:border-[#C8A24A]/40 transition-colors"
        data-testid="ss-permission-label"
      >
        <input
          type="checkbox"
          checked={form.permission}
          onChange={(e) => update({ permission: e.target.checked })}
          className="mt-1 w-5 h-5 accent-[#C8A24A]"
          data-testid="ss-permission"
        />
        <span className="text-sm text-white/85 leading-relaxed">
          I understand this documentary may be streamed, promoted, distributed,
          and used in marketing materials if selected.
        </span>
      </label>
    </div>
  );
}

function ThankYou() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="text-center py-10"
      data-testid="set-speaks-thankyou"
    >
      <CheckCircle2 className="w-14 h-14 text-[#C8A24A] mx-auto mb-6" />
      <h2
        className="font-heading text-3xl md:text-5xl font-black uppercase tracking-tight leading-[0.95]"
        style={{
          backgroundImage:
            "linear-gradient(180deg, #f4eedf 0%, #d6c896 60%, #8a6f29 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Thank You
        <br />
        For Speaking
        <br />
        On The Culture.
      </h2>
      <p className="mt-8 text-white/75 max-w-md mx-auto">
        If selected, our production team will contact you.
      </p>
      <div className="mt-12 font-accent text-[11px] tracking-[0.34em] text-[#C8A24A] italic">
        "The set remembers everything."
      </div>
      <Link
        to="/"
        className="mt-12 inline-flex items-center gap-2 text-white/55 hover:text-white font-accent text-xs tracking-[0.28em]"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> BACK TO AUTOMOTOVILLE
      </Link>
    </motion.div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#C8A24A]/15 py-10 px-6 text-center">
      <div className="font-accent text-[11px] tracking-[0.28em] text-[#C8A24A]/85">
        AUTOMOTOVILLE ORIGINAL DOCUMENTARY SERIES
      </div>
      <div className="mt-2 text-white/35 text-xs">
        © {new Date().getFullYear()} AutoMotoVille
      </div>
    </footer>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className={labelCls}>{label}</div>
      {children}
    </label>
  );
}
